/**
 * Client hooks — TanStack Query wrappers for the clients API.
 *
 * Hooks:
 * - useClients(filters?)    — paginated list of clients with optional filters
 * - useClient(id)           — single client by ID
 * - useCreateClient()       — mutation to create a new client
 * - useUpdateClient()       — mutation to update a client's fields
 * - useReassignClient()     — mutation to reassign a client to a new owner (admin only)
 *
 * Note: the API returns snake_case rows (ClientRow), NOT the camelCase Client interface.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '@/lib/api'
import type { ClientRow, CreateClientDTO, UpdateClientDTO } from '@1klick/types'

// ── Filter types ─────────────────────────────────────────────────────────────

export interface ClientListFilters {
  status?: string
  channel?: string
  search?: string
  page?: number
  limit?: number
}

// ── Response types ────────────────────────────────────────────────────────────

/** API returns { data: ClientRow[] } for the list endpoint */
interface ClientsListApiResponse {
  data: ClientRow[]
}

/** API returns { data: ClientRow } for single-client endpoints */
interface ClientDetailApiResponse {
  data: ClientRow
}

// ── Query key factory ─────────────────────────────────────────────────────────

const clientKeys = {
  all: ['clients'] as const,
  list: (filters?: ClientListFilters) => [...clientKeys.all, 'list', filters] as const,
  detail: (id: string) => [...clientKeys.all, 'detail', id] as const,
}

// ── Paginated list result ─────────────────────────────────────────────────────

export interface ClientsListResult {
  data: ClientRow[]
  total: number
  page: number
  limit: number
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated, filtered list of all clients.
 * Cross-visibility: all authenticated users see all non-deleted clients.
 */
export function useClients(filters?: ClientListFilters) {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.channel) params.set('channel', filters.channel)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters?.page !== undefined) {
    const limit = filters.limit ?? 25
    const offset = (filters.page - 1) * limit
    params.set('offset', String(offset))
  }
  const qs = params.toString()
  const path = `/api/v1/clients${qs ? `?${qs}` : ''}`

  return useQuery<ClientsListResult>({
    queryKey: clientKeys.list(filters),
    queryFn: async () => {
      const res = await apiGet<ClientsListApiResponse>(path)
      return {
        data: res.data,
        total: res.data.length,
        page: filters?.page ?? 1,
        limit: filters?.limit ?? 25,
      }
    },
  })
}

/**
 * Fetch a single client by ID.
 * Disabled when `id` is falsy (empty string, undefined).
 */
export function useClient(id: string) {
  return useQuery<ClientRow>({
    queryKey: clientKeys.detail(id),
    queryFn: async () => {
      const res = await apiGet<ClientDetailApiResponse>(`/api/v1/clients/${id}`)
      return res.data
    },
    enabled: !!id,
  })
}

/**
 * Mutation to create a new client.
 * Invalidates the clients list cache on success.
 */
export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation<ClientRow, Error, CreateClientDTO>({
    mutationFn: async (data) => {
      const res = await apiPost<ClientDetailApiResponse>('/api/v1/clients', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
    },
  })
}

/**
 * Mutation to update a client.
 * Invalidates both the list and the individual detail cache on success.
 */
export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation<ClientRow, Error, { id: string; data: UpdateClientDTO }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<ClientDetailApiResponse>(`/api/v1/clients/${id}`, data)
      return res.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.id) })
    },
  })
}

/**
 * Mutation to reassign a client to a new owner.
 * Admin-only — API enforces via requireRole middleware.
 * Invalidates all client queries on success.
 */
export function useReassignClient() {
  const queryClient = useQueryClient()

  return useMutation<ClientRow, Error, { id: string; newOwnerId: string }>({
    mutationFn: async ({ id, newOwnerId }) => {
      const res = await apiPut<ClientDetailApiResponse>(`/api/v1/clients/${id}/reassign`, {
        newOwnerId,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
    },
  })
}
